import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { Helmet } from "react-helmet";
import {
  Box,
  Container,
  Typography,
  Stack,
  Button,
  Link,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
  alpha,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import PageTransition from "../components/PageTransition";
import ScrollReveal from "../components/ScrollReveal";
import { CardDiagram } from "../components/StudioDiagrams";
import CommitChart from "../components/CommitChart";
import { DEK, HINT, CARDS, SHOTS, RUNNING_INTRO, OUTPUT_EYEBROW, OUTPUT_TITLE, OUTPUT_INTRO, OUTPUT_AFTER, ORCHESTRATORS_INTRO, ORCHESTRATOR_NOTES, WRITING_EYEBROW, WRITING_TITLE, WRITING_INTRO, WRITING_LINKS, ACCENT, ACCENT_SOFT, SERIF, MEASURE } from "../data/studioStory";

function Eyebrow({ children, sx }) {
  return (
    <Typography
      component="div"
      sx={{
        fontSize: "0.72rem",
        textTransform: "uppercase",
        letterSpacing: "0.2em",
        fontWeight: 600,
        color: "text.secondary",
        ...sx,
      }}
    >
      {children}
    </Typography>
  );
}

function StudioCard({ card }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <Box id={`part-${card.n}`} sx={{ scrollMarginTop: "24px" }}>
      <Accordion
        disableGutters
        elevation={0}
        square={false}
        // MUI wraps the summary in its own heading element (default h3), which
        // would skip a level under the page h1. The card title inside is a
        // <span> so we don't nest a heading in a heading.
        slotProps={{ heading: { component: "h2" } }}
        sx={{
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          backgroundColor: isDark
            ? alpha(theme.palette.common.white, 0.02)
            : theme.palette.common.white,
          "&:before": { display: "none" },
          overflow: "hidden",
          transition: "border-color 0.2s ease",
          "&:hover": { borderColor: alpha(ACCENT, 0.5) },
          "&.Mui-expanded": { borderColor: alpha(ACCENT, 0.5) },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: "accent.text" }} />}
          aria-controls={`part-${card.n}-content`}
          id={`part-${card.n}-header`}
          sx={{
            px: { xs: 2.5, md: 3 },
            py: 1,
            alignItems: "flex-start",
            "& .MuiAccordionSummary-content": { my: 2.5 },
            "& .MuiAccordionSummary-expandIconWrapper": { mt: 2.5 },
          }}
        >
          <Box>
            <Stack direction="row" spacing={1.5} alignItems="baseline">
              <Typography
                component="span"
                sx={{
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  letterSpacing: "0.2em",
                  color: "accent.text",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {card.n}
              </Typography>
              <Eyebrow>{card.eyebrow}</Eyebrow>
            </Stack>
            <Typography
              component="span"
              display="block"
              sx={{
                mt: 1,
                fontFamily: SERIF,
                fontWeight: 600,
                fontSize: { xs: "1.3rem", sm: "1.5rem" },
                lineHeight: 1.25,
                color: "text.primary",
              }}
            >
              {card.title}
            </Typography>
            <Typography
              sx={{
                mt: 1.25,
                color: "text.secondary",
                lineHeight: 1.7, maxWidth: MEASURE,
              }}
            >
              {card.what}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ px: { xs: 2.5, md: 3 }, pb: 3, pt: 0 }}>
          <Box
            sx={{
              borderTop: `1px solid ${theme.palette.divider}`,
              pt: 2.5,
            }}
          >
            {card.diagram && <CardDiagram which={card.diagram} />}
            <Stack spacing={2}>
              {card.how.map((para, i) => (
                <Typography
                  key={i}
                  sx={{ color: "text.primary", lineHeight: 1.75, maxWidth: MEASURE }}
                >
                  {para}
                </Typography>
              ))}
            </Stack>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}

function StudioPage() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <PageTransition>
      <Helmet>
        <title>The Studio – How David Kooi runs a product studio, solo</title>
        <link rel="canonical" href="https://davidkooi.com/studio/" />
        <meta
          name="description"
          content="A one-person, AI-native product studio: how one founder runs a portfolio of products with a fleet of AI agents — the operating model, the daily loop, and how quality stays honest."
        />
        <meta property="og:type" content="article" />
        <meta property="og:title" content="Inside the studio – David Kooi" />
        <meta
          property="og:description"
          content="The map of a one-person, AI-native product studio: one founder, a fleet of AI agents, products built to be loved."
        />
        <meta property="og:url" content="https://davidkooi.com/studio/" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Inside the studio – David Kooi" />
        <meta
          name="twitter:description"
          content="One founder, a fleet of AI agents: the operating model, the daily loop, and how quality stays honest."
        />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: "Inside the studio: how David Kooi runs a product studio, solo",
            description: DEK,
            mainEntityOfPage: {
              "@type": "WebPage",
              "@id": "https://davidkooi.com/studio",
            },
            url: "https://davidkooi.com/studio",
            inLanguage: "en",
            author: {
              "@type": "Person",
              name: "David Kooi",
              url: "https://davidkooi.com",
              jobTitle: "Founder, Skylark Creations",
            },
            publisher: {
              "@type": "Organization",
              name: "Skylark Creations",
              url: "https://skylarkcreations.com",
            },
            about: [
              "AI-native product development",
              "Agentic software development",
              "Solo founder operating model",
              "Product quality",
            ],
            // Derived from the cards themselves so the structured data can't
            // drift away from what the page actually says.
            hasPart: CARDS.map((card) => ({
              "@type": "WebPageElement",
              name: card.title,
              url: `https://davidkooi.com/studio#part-${card.n}`,
              description: card.what,
            })),
          })}
        </script>
      </Helmet>

      <Box sx={{ minHeight: "100vh", backgroundColor: "background.default" }}>
        <Navigation />

        <Box component="main" id="main">
          {/* Hero */}
          <Container maxWidth="md" sx={{ pt: { xs: 6, md: 8 }, pb: 4 }}>
            <ScrollReveal>
              <Box>
                <Eyebrow>Inside the studio</Eyebrow>
                <Typography
                  component="h1"
                  sx={{
                    mt: 1.5,
                    fontFamily: SERIF,
                    fontWeight: 600,
                    fontSize: { xs: "2.25rem", sm: "3rem" },
                    lineHeight: 1.1,
                    letterSpacing: "-0.01em",
                    color: "text.primary",
                  }}
                >
                  How the studio runs
                </Typography>
                <Typography
                  sx={{
                    mt: 3,
                    fontSize: { xs: "1.1rem", md: "1.2rem" },
                    lineHeight: 1.6, maxWidth: MEASURE,
                    color: "text.primary",
                  }}
                >
                  {DEK}
                </Typography>
                <Typography sx={{ mt: 2, fontSize: "0.9rem", color: "text.secondary" }}>
                  Skylark Creations · a one-person studio · based in Colorado
                </Typography>
                <Typography sx={{ mt: 3, fontSize: "0.9rem", color: "text.secondary" }}>
                  {HINT}
                </Typography>

                {/* Contents — orientation + jump-to */}
                <Box
                  component="nav"
                  aria-label="Contents"
                  sx={{
                    mt: 3,
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.divider}`,
                    backgroundColor: isDark
                      ? alpha(theme.palette.common.white, 0.02)
                      : alpha(theme.palette.common.black, 0.015),
                    p: 2.5,
                  }}
                >
                  <Eyebrow>The ten parts</Eyebrow>
                  <Box
                    component="ol"
                    sx={{
                      listStyle: "none",
                      m: 0,
                      mt: 1.5,
                      p: 0,
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                      columnGap: 3,
                      rowGap: 0.75,
                    }}
                  >
                    {CARDS.map((card) => (
                      <Box component="li" key={card.n}>
                        <Link
                          href={`#part-${card.n}`}
                          underline="none"
                          sx={{
                            display: "flex",
                            gap: 1,
                            fontSize: "0.9rem",
                            color: "text.secondary",
                            "&:hover": { color: "text.primary" },
                            "&:hover .ttl": { textDecoration: "underline" },
                          }}
                        >
                          <Box component="span" sx={{ color: "accent.text", fontVariantNumeric: "tabular-nums" }}>
                            {card.n}
                          </Box>
                          <Box component="span" className="ttl">
                            {card.title}
                          </Box>
                        </Link>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            </ScrollReveal>
          </Container>

          {/* The ten cards */}
          <Container maxWidth="md" sx={{ pb: 6 }}>
            <Stack spacing={2}>
              {CARDS.map((card) => (
                <StudioCard key={card.n} card={card} />
              ))}
            </Stack>
          </Container>

          {/* Output over time — the commit chart */}
          <Box sx={{ borderTop: `1px solid ${theme.palette.divider}` }}>
            <Container maxWidth="md" sx={{ py: 6 }}>
              <ScrollReveal>
                <Box>
                  <Eyebrow>{OUTPUT_EYEBROW}</Eyebrow>
                  <Typography
                    component="h2"
                    sx={{
                      mt: 1,
                      fontFamily: SERIF,
                      fontWeight: 600,
                      fontSize: { xs: "1.6rem", sm: "2rem" },
                      color: "text.primary",
                    }}
                  >
                    {OUTPUT_TITLE}
                  </Typography>
                  <Typography sx={{ mt: 2, mb: 3, lineHeight: 1.75, color: "text.primary", maxWidth: MEASURE }}>
                    {OUTPUT_INTRO}
                  </Typography>

                  <CommitChart />

                  <Stack spacing={2} sx={{ mt: 3 }}>
                    {OUTPUT_AFTER.map((para, i) => (
                      <Typography key={i} sx={{ color: "text.primary", lineHeight: 1.75, maxWidth: MEASURE }}>
                        {para}
                      </Typography>
                    ))}
                  </Stack>
                </Box>
              </ScrollReveal>
            </Container>
          </Box>

          {/* See it running */}
          <Box sx={{ borderTop: `1px solid ${theme.palette.divider}` }}>
            <Container maxWidth="md" sx={{ py: 6 }}>
              <ScrollReveal>
                <Box>
                  <Eyebrow>See it running</Eyebrow>
                  <Typography
                    component="h2"
                    sx={{
                      mt: 1,
                      fontFamily: SERIF,
                      fontWeight: 600,
                      fontSize: { xs: "1.6rem", sm: "2rem" },
                      color: "text.primary",
                    }}
                  >
                    The studio, live
                  </Typography>
                  <Typography sx={{ mt: 2, lineHeight: 1.75, color: "text.primary", maxWidth: MEASURE }}>
                    {RUNNING_INTRO}
                  </Typography>

                  <Stack spacing={4} sx={{ mt: 4 }}>
                    {SHOTS.map((shot) => (
                      <Box component="figure" key={shot.src} sx={{ m: 0 }}>
                        <Box
                          component="img"
                          src={shot.src}
                          alt={shot.alt}
                          loading="lazy"
                          sx={{
                            width: "100%",
                            height: "auto",
                            display: "block",
                            borderRadius: 2,
                            border: `1px solid ${theme.palette.divider}`,
                          }}
                        />
                        <Typography
                          component="figcaption"
                          sx={{ mt: 1, fontSize: "0.85rem", color: "text.secondary", maxWidth: MEASURE }}
                        >
                          {shot.caption}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              </ScrollReveal>
            </Container>
          </Box>

          {/* From the orchestrator's seat */}
          <Box sx={{ borderTop: `1px solid ${theme.palette.divider}` }}>
            <Container maxWidth="md" sx={{ py: 6 }}>
              <ScrollReveal>
                <Box>
                  <Eyebrow>From the orchestrator&rsquo;s seat</Eyebrow>
                  <Typography
                    component="h2"
                    sx={{
                      mt: 1,
                      fontFamily: SERIF,
                      fontWeight: 600,
                      fontSize: { xs: "1.6rem", sm: "2rem" },
                      color: "text.primary",
                    }}
                  >
                    The system, in its own words
                  </Typography>
                  <Typography sx={{ mt: 2, lineHeight: 1.75, color: "text.primary", maxWidth: MEASURE }}>
                    {ORCHESTRATORS_INTRO}
                  </Typography>

                  <Stack spacing={3} sx={{ mt: 4 }}>
                    {ORCHESTRATOR_NOTES.map((note) => (
                      <Box
                        key={note.id}
                        sx={{
                          borderLeft: `3px solid ${ACCENT}`,
                          pl: { xs: 2.5, md: 3 },
                          py: 0.5,
                        }}
                      >
                        <Typography sx={{ fontWeight: 600, color: "text.primary" }}>
                          {note.label}
                        </Typography>
                        <Typography sx={{ fontSize: "0.85rem", color: "text.secondary" }}>
                          {note.sub}
                        </Typography>
                        <Stack spacing={2} sx={{ mt: 1.5 }}>
                          {note.paras.map((para, i) => (
                            <Typography key={i} sx={{ color: "text.primary", lineHeight: 1.75, maxWidth: MEASURE }}>
                              {para}
                            </Typography>
                          ))}
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              </ScrollReveal>
            </Container>
          </Box>

          {/* Coding with AI — the writing behind the method */}
          <Box sx={{ borderTop: `1px solid ${theme.palette.divider}` }}>
            <Container maxWidth="md" sx={{ py: 6 }}>
              <ScrollReveal>
                <Box>
                  <Eyebrow>{WRITING_EYEBROW}</Eyebrow>
                  <Typography
                    component="h2"
                    sx={{
                      mt: 1,
                      fontFamily: SERIF,
                      fontWeight: 600,
                      fontSize: { xs: "1.6rem", sm: "2rem" },
                      color: "text.primary",
                    }}
                  >
                    {WRITING_TITLE}
                  </Typography>
                  <Typography sx={{ mt: 2, lineHeight: 1.75, color: "text.primary", maxWidth: MEASURE }}>
                    {WRITING_INTRO}
                  </Typography>

                  <Stack spacing={2.5} sx={{ mt: 3 }}>
                    {WRITING_LINKS.map((item) => (
                      <Box key={item.href}>
                        <Link
                          href={item.href}
                          target="_blank"
                          rel="noopener"
                          underline="hover"
                          sx={{ fontWeight: 600, color: "accent.text", fontSize: "1.05rem" }}
                        >
                          {item.label}
                        </Link>
                        <Typography sx={{ mt: 0.5, color: "text.secondary", lineHeight: 1.7, maxWidth: MEASURE }}>
                          {item.note}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              </ScrollReveal>
            </Container>
          </Box>

          {/* Closing CTA */}
          <Box sx={{ borderTop: `1px solid ${theme.palette.divider}` }}>
            <Container maxWidth="md" sx={{ py: 6 }}>
              <ScrollReveal>
                <Box
                  sx={{
                    borderRadius: 3,
                    border: `1px solid ${alpha(ACCENT, 0.35)}`,
                    backgroundColor: ACCENT_SOFT,
                    p: { xs: 3, md: 4 },
                    textAlign: "center",
                  }}
                >
                  <Typography
                    component="h2"
                    sx={{
                      fontFamily: SERIF,
                      fontWeight: 600,
                      fontSize: { xs: "1.5rem", sm: "1.9rem" },
                      color: "text.primary",
                    }}
                  >
                    This is the engine. Here's what it builds.
                  </Typography>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={2}
                    justifyContent="center"
                    sx={{ mt: 3 }}
                  >
                    <Button
                      href="https://skylarkcreations.com"
                      target="_blank"
                      rel="noopener"
                      variant="contained"
                      sx={{
                        px: 3,
                        py: 1.25,
                        backgroundColor: ACCENT,
                        color: "accent.contrastText",
                        "&:hover": { backgroundColor: "hsl(26 95% 44%)" },
                      }}
                    >
                      See the products
                    </Button>
                    <Button
                      component={RouterLink}
                      to="/chat"
                      variant="outlined"
                      sx={{
                        px: 3,
                        py: 1.25,
                        borderColor: alpha(ACCENT, 0.5),
                        color: "text.primary",
                        "&:hover": {
                          borderColor: ACCENT,
                          backgroundColor: alpha(ACCENT, 0.08),
                        },
                      }}
                    >
                      Chat with ChatDDK
                    </Button>
                  </Stack>
                  <Box sx={{ mt: 2.5 }}>
                    <Link
                      component={RouterLink}
                      to="/"
                      underline="hover"
                      sx={{ fontSize: "0.9rem", color: "text.secondary" }}
                    >
                      ← Back to home
                    </Link>
                  </Box>
                </Box>
              </ScrollReveal>
            </Container>
          </Box>
        </Box>

        <Footer />
      </Box>
    </PageTransition>
  );
}

export default StudioPage;
